
import { AxiosError } from "axios";
import { computed, defineComponent, getCurrentInstance, ref } from "vue";
import { MetaInfo } from "vue-meta";
import { NavigationGuardNext, Route } from "vue-router";
import { onBeforeRouteUpdate } from "vue-router/composables";
import { useBaseView } from "@composables/baseView";
import { useI18n } from "@composables/i18n";
import { useLoader } from "@composables/loader";
import SharingLogo from "@images/logos/sharing/logo_white.png";
import { alternateService } from "@services/alternate.service";
import { apiService } from "@services/api.service";
import { ciTrackingService } from "@services/ci-tracking.service";
import { gtmTrackingService } from "@services/gtm-tracking.service";
import { ImgixService } from "@services/imgix.service";
import UserProfile from "./profiles/profile.vue";

export default defineComponent({
  name: "CiUserProfileWrapper",
  components: {
    "ci-user-profile": UserProfile,
  },
  metaInfo(): MetaInfo {
    return {
      title: this.metaOptions.metaTitle,
      link: alternateService.fromRoute(this.$route),
      meta: [
        { vmid: "description", name: "description", content: this.metaOptions.metaDescription },
        { vmid: "og:title", property: "og:title", content: this.metaOptions.metaTitle },
        { vmid: "og:description", property: "og:description", content: this.metaOptions.metaDescription },
        { vmid: "og:image", property: "og:image", content: this.metaOptions.metaImage },
        { vmid: "twitter:title", name: "twitter:title", content: this.metaOptions.metaTitle },
        { vmid: "twitter:description", name: "twitter:description", content: this.metaOptions.metaDescription },
        { vmid: "twitter:image", name: "twitter:image", content: this.metaOptions.metaImage },
      ],
    };
  },
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext) {
    const loader = useLoader();

    loader.start();

    try {
      next(async (vm: any) => vm.handleRoute(to, from));
    } catch (err) {
      apiService.handleRoutingAPIError(err as AxiosError, (routerError) => loader.setError(routerError));
      next();
    } finally {
      loader.stop();
    }
  },
  setup() {
    const user = ref<Nullable<UsersV2.User>>(null);
    const { t } = useI18n(getCurrentInstance());
    const loader = useLoader();
    const { initialized, onBeforeMount, onBeforeUnmount, onBeforeRouteLeave } = useBaseView();

    const metaOptions = computed(() => {
      const title = [user.value?.display_name, user.value?.position, user.value?.company.name]
        .filter((field) => !!field)
        .join(" - ");
      const userImage = user.value?.images?.avatar
        ? ImgixService.image(user.value?.images?.avatar)
        : window.location.origin + SharingLogo;

      return {
        metaTitle: title,
        metaDescription: t("ui.views.users.show.meta_tags.description", { name: user.value?.display_name }) as string,
        metaImage: userImage,
      };
    });

    onBeforeRouteUpdate(async (to: Route, from: Route, next: NavigationGuardNext) => {
      if (from.params?.slug !== to.params?.slug) {
        loader.start();

        try {
          await handleRoute(to, from);
        } catch (err) {
          apiService.handleRoutingAPIError(err as AxiosError, (routerError) => loader.setError(routerError));
        } finally {
          loader.stop();
        }
      }
      next();
    });

    const manualGTMUpdate = () => true;

    const handleRoute = async (to: Route, from: Route) => {
      const response = await apiService.users.get(to.params.slug);

      const userType = response.data.type;
      const userTypeForPageType = (userType === "advisor" && userType) || "basic";
      const trackingTargetName = (userType === "advisor" && "UserProfileAdvisor") || "UserProfileBasic";

      user.value = response.data;
      initialized.value = true;
      if (from.name) {
        gtmTrackingService.reinitialize(from.fullPath, to.meta?.pageType[userTypeForPageType]);
      }

      await ciTrackingService.trackView(trackingTargetName, response.data.id.toString());
    };

    return {
      user,
      initialized,
      metaOptions,
      manualGTMUpdate,
      handleRoute,
      onBeforeMount,
      onBeforeUnmount,
      onBeforeRouteLeave,
      onBeforeRouteUpdate,
    };
  },
});
